import { Decoration, ViewPlugin, EditorView } from '@codemirror/view';
import { getCommentLineRanges, isInCommentRange } from './commentUtils.js';

/**
 * Claim Number Highlighter Extension
 * 
 * Highlights claim numbers and subnumbers at the start of lines
 * with bold text and the same color as patent keywords
 * - Main claims: 1., 2., etc.
 * - Letter subnumbers: (a), (b), etc.
 * - Roman subnumbers: (i), (ii), etc.
 */

// Patterns to match different numbering levels
const CLAIM_NUMBER_PATTERN = /^(\d+)\./;
const _LETTER_PATTERN = /^(\s*)\(([a-z])\)/;
const _ROMAN_PATTERN = /^(\s*)\(([ivx]+)\)/i;

// Create decoration for highlighted claim numbers
const claimNumberMark = Decoration.mark({
  class: "cm-claim-number"
});

// Create the highlighting plugin
const claimNumberHighlighter = ViewPlugin.fromClass(class {
  constructor(view) {
    this.decorations = this.highlight(view);
  }

  update(update) {
    if (update.docChanged || update.viewportChanged) {
      this.decorations = this.highlight(update.view);
    }
  }

  highlight(view) {
    const builder = [];
    
    // Get comment ranges to exclude from highlighting
    const commentRanges = getCommentLineRanges(view);
    
    // Process each line in the document
    for (let i = 1; i <= view.state.doc.lines; i++) {
      const line = view.state.doc.line(i);
      const lineText = line.text;
      
      // Skip this line entirely if it's a comment
      if (isInCommentRange(line.from, commentRanges)) {
        continue;
      }
      
      // Check for main claim number at line start only
      const claimMatch = lineText.match(CLAIM_NUMBER_PATTERN);
      if (claimMatch) {
        // Highlight the entire number including the period
        const start = line.from;
        const end = line.from + claimMatch[0].length;
        builder.push(claimNumberMark.range(start, end));
      }
      
      // Find ALL letter patterns (a), (b), etc. anywhere in the line
      const letterRegex = /\(([a-z])\)/g;
      let letterMatch;
      while ((letterMatch = letterRegex.exec(lineText)) !== null) {
        const start = line.from + letterMatch.index;
        const end = start + letterMatch[0].length;
        builder.push(claimNumberMark.range(start, end));
      }
      
      // Find ALL roman patterns (i), (ii), etc. anywhere in the line
      const romanRegex = /\(([ivx]+)\)/gi;
      let romanMatch;
      while ((romanMatch = romanRegex.exec(lineText)) !== null) {
        const start = line.from + romanMatch.index;
        const end = start + romanMatch[0].length;
        builder.push(claimNumberMark.range(start, end));
      }
    }
    
    return Decoration.set(builder);
  }
}, {
  decorations: v => v.decorations
});

// Add the highlighting styles - same as patent keywords
const claimNumberStyles = EditorView.theme({
  ".cm-claim-number": {
    color: "var(--color-patent-keyword)",
    fontWeight: "bold"
  }
}, { dark: false });

// Export the extension
export const claimNumberHighlighterExtension = [
  claimNumberHighlighter,
  claimNumberStyles
];